// 1. React imports
import { useState } from 'react';

// 2. External libraries
import { CircleCheck } from 'lucide-react';

// 3. Internal components
import { BrandCard } from '~/components/common';
import { WizardActions } from '~/components/onboarding/WizardActions';

/**
 * Props for WelcomeStep component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface WelcomeStepProps {
  onNext: () => void;
  onSkip: () => void;
}

/**
 * Configuration for accomplishments displayed in welcome step.
 *
 * Defines the tasks users will complete during the Telegram onboarding demo.
 */
interface AccomplishmentConfig {
  id: string;
  label: string;
}

/**
 * Accomplishments users will achieve during Telegram demo.
 *
 * Per task spec: create topic, connect Telegram, send message, retrieve message.
 */
const ACCOMPLISHMENTS: AccomplishmentConfig[] = [
  {
    id: 'create_topic',
    label: 'Create a topic for message delivery',
  },
  {
    id: 'connect_telegram',
    label: 'Connect your Telegram account',
  },
  {
    id: 'send_message',
    label: 'Send a message via Telegram',
  },
  {
    id: 'retrieve_message',
    label: 'Retrieve the message from your topic',
  },
];

/**
 * Welcome step for Telegram onboarding demo.
 *
 * Displays welcome heading, lists 4 accomplishments.
 * Get Started button advances to next step. Skip button exits wizard.
 *
 * Per §TS.3.1: Function declaration for named export.
 *
 * @example
 * ```tsx
 * <WelcomeStep
 *   onNext={() => setStep(2)}
 *   onSkip={handleSkip}
 * />
 * ```
 */
export function WelcomeStep({ onNext, onSkip }: WelcomeStepProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div>
      <p className="mb-4 text-sm text-muted-foreground">
        We're glad you're here! Let's get you started!
      </p>
      <p className="mb-4 text-sm text-muted-foreground">
        DeadDrop makes it easy to receive messages from online services and process them locally
        with your own tools without having to run or expose your own servers. In this tutorial,
        we'll cover some basic concepts and get you up and running with a simple setup that lets you
        process Telegram messages with your own LLM client.
      </p>
      <p className="mb-8 text-sm text-muted-foreground">
        We will guide you through the following steps:
      </p>

      {/* Accomplishments List */}
      <div className="mb-6 flex flex-col gap-4">
        {ACCOMPLISHMENTS.map((accomplishment) => {
          const isHovered = hoveredId === accomplishment.id;

          return (
            <BrandCard
              key={accomplishment.id}
              accent="none"
              glow={isHovered ? 'subtle' : 'none'}
              onMouseEnter={() => setHoveredId(accomplishment.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="flex items-center gap-4 border-border bg-card p-4"
            >
              <CircleCheck className="size-6 text-muted-foreground" />
              <p className="text-base text-foreground">{accomplishment.label}</p>
            </BrandCard>
          );
        })}
      </div>

      {/* Wizard Actions */}
      <WizardActions onNext={onNext} onSkip={onSkip} nextLabel="Get Started" showBack={false} />
    </div>
  );
}
